import React, { useState, useRef } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Input,
  Divider,
  Checkbox,
  Link,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { Turnstile } from '@marsidev/react-turnstile';
import { useAuth } from '../contexts/AuthContext';
import { getFirebaseErrorMessage, shouldShowError } from '../utils/firebaseErrors';

const LoginModal = ({ isOpen, onClose, onSwitchToSignUp }) => {
  const { login, signInWithGoogle, resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef();

  // Detect mobile for scroll lock fix
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
    ('ontouchstart' in window) ||
    (window.innerWidth <= 768);

  const togglePasswordVisibility = () => setIsPasswordVisible(!isPasswordVisible);

  // Get Turnstile site key based on environment
  const getTurnstileSiteKey = () => {
    const env = process.env.REACT_APP_ENV || 'development';
    return env === 'production'
      ? process.env.REACT_APP_TURNSTILE_SITE_KEY_PROD
      : process.env.REACT_APP_TURNSTILE_SITE_KEY_DEV;
  };

  const inputClasses = {
    inputWrapper: [
      "border-transparent bg-default-100/50 dark:bg-default-100/20",
      "group-data-[focus=true]:border-primary group-data-[focus=true]:bg-background/80",
      "group-data-[focus=true]:shadow-lg group-data-[focus=true]:shadow-primary/20",
      "data-[hover=true]:border-foreground/30 data-[hover=true]:bg-default-50",
      "transition-all duration-200",
    ].join(" "),
    label: "text-foreground/80 font-medium",
    input: "text-foreground placeholder:text-foreground/40",
  };

  const buttonClasses = "bg-foreground/10 dark:bg-foreground/20";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Turnstile verification
    if (!turnstileToken) {
      return setError('Please complete the security verification');
    }

    try {
      setLoading(true);
      await login(email, password);
      onClose();
      setEmail('');
      setPassword('');
    } catch (err) {
      // ✅ FIX: Reset Turnstile token on auth failure
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken('');

      const errorMessage = getFirebaseErrorMessage(err);
      if (errorMessage && shouldShowError(err)) {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      await signInWithGoogle();
      onClose();
    } catch (err) {
      const errorMessage = getFirebaseErrorMessage(err);
      if (errorMessage && shouldShowError(err)) {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email to reset password');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await resetPassword(email);
      setResetEmailSent(true);
    } catch (err) {
      const errorMessage = getFirebaseErrorMessage(err);
      if (errorMessage && shouldShowError(err)) {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // On mobile, render as normal page component - NO modal
  if (isMobile && isOpen) {
    return (
      <div className="fixed inset-0 z-50 min-h-screen bg-gradient-to-br from-primary-50/40 via-background to-secondary-50/30 dark:from-primary-900/20 dark:via-background dark:to-secondary-900/10 flex items-center justify-center overflow-y-auto py-4">
        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 z-[60] bg-background/80 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-content1 rounded-2xl p-8 shadow-xl flex flex-col items-center gap-4">
              <Icon icon="solar:refresh-circle-bold" className="animate-spin text-primary text-5xl" />
              <p className="text-foreground/80 font-medium">Signing in...</p>
            </div>
          </div>
        )}

        <div className="w-full max-w-md px-4">
          {/* Welcome Section with Branding - Compact */}
          <div className="text-center mb-4">
            {/* Logo/Brand Icon - Smaller */}
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-lg">
              <Icon icon="solar:video-library-bold" className="text-white text-2xl" />
            </div>

            {/* Welcome Message - Compact */}
            <div className="px-6 py-3 rounded-2xl" style={{ background: 'rgba(0,0,0,0.25)' }}>
              <h1 className="text-xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 dark:from-primary-400 dark:to-secondary-400 bg-clip-text text-transparent mb-1">
                Welcome Back
              </h1>
              <p className="text-xs text-foreground/60">
                Sign in to continue sharing moments
              </p>
            </div>
          </div>

          {/* Elevated Form Container with Glass Effect - Compact Padding */}
          <div className="bg-content1/80 backdrop-blur-xl backdrop-saturate-150 rounded-2xl p-4 shadow-medium border border-divider/50">

            {/* Social Login - Primary Action - Compact */}
            <div className="mb-4">
              <Button
                size="md"
                fullWidth
                className="bg-white dark:bg-content2 border-2 border-default-200 hover:border-primary hover:shadow-lg hover:shadow-primary/20 hover:scale-[1.02] transition-all duration-200"
                startContent={<Icon icon="fe:google" width={20} />}
                onPress={handleGoogleSignIn}
                isDisabled={loading}
              >
                <span className="text-foreground font-medium text-sm">Continue with Google</span>
              </Button>
            </div>

            {/* Divider - Compact */}
            <div className="flex items-center gap-3 mb-4">
              <Divider className="flex-1" />
              <p className="text-xs font-medium text-foreground/50 uppercase tracking-wide">
                Or use email
              </p>
              <Divider className="flex-1" />
            </div>

            {/* Error Messages with Icons */}
            {error && (
              <div className="bg-danger-50/80 dark:bg-danger-500/10 border-l-4 border-danger-500 text-danger-700 dark:text-danger-400 px-4 py-3 rounded-lg backdrop-blur-sm flex items-start gap-3 mb-4 animate-in slide-in-from-top-2 duration-300">
                <Icon icon="solar:danger-circle-bold" className="text-danger-500 text-xl mt-0.5 flex-shrink-0" />
                <p className="text-sm flex-1">{error}</p>
              </div>
            )}

            {resetEmailSent && (
              <div className="bg-success-50/80 dark:bg-success-500/10 border-l-4 border-success-500 text-success-700 dark:text-success-400 px-4 py-3 rounded-lg backdrop-blur-sm flex items-start gap-3 mb-4 animate-in slide-in-from-top-2 duration-300">
                <Icon icon="solar:check-circle-bold" className="text-success-500 text-xl mt-0.5 flex-shrink-0" />
                <p className="text-sm flex-1">Password reset email sent! Check your inbox.</p>
              </div>
            )}

          <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
            <Input
              isRequired
              size="sm"
              classNames={{
                ...inputClasses,
                input: isMobile ? "text-base" : ""
              }}
              label="Email Address"
              name="email"
              placeholder="Enter your email"
              type="email"
              variant="bordered"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Input
              isRequired
              size="sm"
              classNames={{
                ...inputClasses,
                input: isMobile ? "text-base" : ""
              }}
              endContent={
                <button type="button" onClick={togglePasswordVisibility}>
                  {isPasswordVisible ? (
                    <Icon
                      className="text-foreground/50 pointer-events-none text-xl"
                      icon="solar:eye-closed-linear"
                    />
                  ) : (
                    <Icon
                      className="text-foreground/50 pointer-events-none text-xl"
                      icon="solar:eye-bold"
                    />
                  )}
                </button>
              }
              label="Password"
              name="password"
              placeholder="Enter your password"
              type={isPasswordVisible ? "text" : "password"}
              variant="bordered"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <div className="flex w-full items-center justify-between px-1 py-1">
              <Checkbox
                classNames={{
                  wrapper: "before:border-foreground/50",
                }}
                name="remember"
                size="sm"
              >
                <span className="text-sm text-foreground/70">Remember me</span>
              </Checkbox>
              <Link
                className="text-foreground/60 hover:text-foreground cursor-pointer"
                size="sm"
                onPress={handleForgotPassword}
                isDisabled={loading}
              >
                Forgot password?
              </Link>
            </div>

            {/* Turnstile Security Verification - Compact */}
            <div className="flex justify-center py-2">
              <Turnstile
                ref={turnstileRef}
                siteKey={getTurnstileSiteKey()}
                onSuccess={(token) => setTurnstileToken(token)}
                onError={() => setTurnstileToken('')}
                onExpire={() => setTurnstileToken('')}
              />
            </div>

            {/* Enhanced Submit Button with Gradient - Compact */}
            <Button
              type="submit"
              size="md"
              fullWidth
              className={[
                "bg-gradient-to-r from-primary-500 to-primary-600",
                "hover:from-primary-600 hover:to-primary-700",
                "shadow-lg shadow-primary/30",
                "hover:shadow-xl hover:shadow-primary/40",
                "hover:scale-[1.02]",
                "transition-all duration-200",
                "font-semibold text-white",
              ].join(" ")}
              isLoading={loading}
              spinner={
                <Icon icon="solar:refresh-circle-bold" className="animate-spin text-lg" />
              }
            >
              {loading ? "Signing In..." : "Log In"}
            </Button>
          </form>

          </div>

          {/* Sign Up Link - Compact */}
          <div className="px-4 py-2 rounded-xl mt-4" style={{ background: 'rgba(0,0,0,0.25)' }}>
            <p className="text-xs text-foreground/60 text-center leading-relaxed">
              Need to create an account?{" "}
              <Link size="sm" className="font-semibold text-primary hover:text-primary-600" onPress={onSwitchToSignUp}>
                Sign Up
              </Link>
            </p>
          </div>

          {/* Close Button - Compact */}
          <div className="px-4 py-2 rounded-xl mt-3" style={{ background: 'rgba(0,0,0,0.25)' }}>
            <Button
              variant="light"
              size="sm"
              onPress={onClose}
              className="text-foreground/60"
              fullWidth
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop: use normal Modal
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="center"
      backdrop="blur"
      size="md"
      scrollBehavior="inside"
      isDismissable={!loading}
      isKeyboardDismissDisabled={loading}
      motionProps={{
        variants: {
          enter: {
            opacity: 1,
            transition: {
              duration: 0.3,
            },
          },
          exit: {
            opacity: 0,
            transition: {
              duration: 0.2,
            },
          },
        },
      }}
    >
      <ModalContent className="rounded-large bg-background/60 shadow-small dark:bg-default-100/50 backdrop-blur-md backdrop-saturate-150">
        {/* Loading Overlay for Desktop */}
        {loading && (
          <div className="absolute inset-0 z-50 bg-background/80 backdrop-blur-sm rounded-large flex items-center justify-center">
            <div className="bg-content1 rounded-2xl p-8 shadow-xl flex flex-col items-center gap-4">
              <Icon icon="solar:refresh-circle-bold" className="animate-spin text-primary text-5xl" />
              <p className="text-foreground/80 font-medium">Signing in...</p>
            </div>
          </div>
        )}

        <div className="flex w-full flex-col gap-4 px-8 pt-6 pb-10">
          <p className="pb-2 text-xl font-medium">Log In</p>

          {error && (
            <div className="bg-danger-50/50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm backdrop-blur-sm">
              {error}
            </div>
          )}

          {resetEmailSent && (
            <div className="bg-success-50/50 border border-success-200 text-success-700 px-4 py-3 rounded-lg text-sm backdrop-blur-sm">
              Password reset email sent! Check your inbox.
            </div>
          )}

          <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
            <Input
              isRequired
              classNames={{
                ...inputClasses,
                input: "text-base"
              }}
              label="Email Address"
              name="email"
              placeholder="Enter your email"
              type="email"
              variant="bordered"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Input
              isRequired
              classNames={{
                ...inputClasses,
                input: "text-base"
              }}
              endContent={
                <button type="button" onClick={togglePasswordVisibility}>
                  {isPasswordVisible ? (
                    <Icon
                      className="text-foreground/50 pointer-events-none text-2xl"
                      icon="solar:eye-closed-linear"
                    />
                  ) : (
                    <Icon
                      className="text-foreground/50 pointer-events-none text-2xl"
                      icon="solar:eye-bold"
                    />
                  )}
                </button>
              }
              label="Password"
              name="password"
              placeholder="Enter your password"
              type={isPasswordVisible ? "text" : "password"}
              variant="bordered"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <div className="flex w-full items-center justify-between px-1 py-2">
              <Checkbox
                classNames={{
                  wrapper: "before:border-foreground/50",
                }}
                name="remember"
                size="sm"
              >
                Remember me
              </Checkbox>
              <Link
                className="text-foreground/50 cursor-pointer"
                size="sm"
                onPress={handleForgotPassword}
                isDisabled={loading}
              >
                Forgot password?
              </Link>
            </div>

            {/* Turnstile Security Verification */}
            <div className="flex justify-center py-2">
              <Turnstile
                ref={turnstileRef}
                siteKey={getTurnstileSiteKey()}
                onSuccess={(token) => setTurnstileToken(token)}
                onError={() => setTurnstileToken('')}
                onExpire={() => setTurnstileToken('')}
              />
            </div>

            <Button
              className={buttonClasses}
              type="submit"
              isLoading={loading}
            >
              Log In
            </Button>
          </form>

          <div className="flex items-center gap-4 py-2">
            <Divider className="flex-1" />
            <p className="text-sm font-medium text-foreground/70 shrink-0">OR</p>
            <Divider className="flex-1" />
          </div>

          <div className="flex flex-col gap-2">
            <Button
              className={buttonClasses}
              startContent={<Icon icon="fe:google" width={24} />}
              onPress={handleGoogleSignIn}
              isDisabled={loading}
            >
              Continue with Google
            </Button>
          </div>

          <p className="text-small text-foreground/50 text-center">
            Need to create an account?&nbsp;
            <Link
              color="foreground"
              size="sm"
              className="cursor-pointer"
              onPress={onSwitchToSignUp}
            >
              Sign Up
            </Link>
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default LoginModal;
